import copy from 'copy-to-clipboard';
import { useCallback, useEffect, useRef, useState } from 'react';

import { IconButton, Icons, tokens } from '@cast/design-system';

/** How long the copy button keeps its "Copied" confirmation icon. */
const COPIED_INDICATOR_DURATION_MS = 3000;

type Props = {
  text: string;
};

export const CopyResponseButton = ({ text }: Props) => {
  const [isCopied, setIsCopied] = useState(false);
  // Hold the active revert timer so rapid clicks restart the 3-second window
  // cleanly and unmount clears any pending callback.
  const copiedResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const handleCopy = useCallback(() => {
    copy(text);
    setIsCopied(true);

    if (copiedResetTimeoutRef.current) {
      clearTimeout(copiedResetTimeoutRef.current);
    }

    copiedResetTimeoutRef.current = setTimeout(() => {
      setIsCopied(false);
      copiedResetTimeoutRef.current = null;
    }, COPIED_INDICATOR_DURATION_MS);
  }, [text]);

  useEffect(() => {
    return () => {
      if (copiedResetTimeoutRef.current) {
        clearTimeout(copiedResetTimeoutRef.current);
        copiedResetTimeoutRef.current = null;
      }
    };
  }, []);

  return (
    <IconButton
      variant="ghost"
      size="sm"
      onClick={handleCopy}
      aria-label={isCopied ? 'Copied' : 'Copy response'}
      testId="chat-copy-response-button"
    >
      {isCopied ? (
        <Icons.Check
          size={14}
          weight="bold"
          color={tokens.theme.fg.action.onGhost}
        />
      ) : (
        <Icons.Copy
          weight="bold"
          size={14}
          color={tokens.theme.fg.action.onGhost}
        />
      )}
    </IconButton>
  );
};
